import { FORM_ERROR } from 'final-form';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import _ from 'lodash';
import { AxiosError } from 'axios';

import logger from '~src/services/logger/LlsLogger';
import { AttributeI } from '~src/interfaces/attributes.types';
import CompanySettingsForm from '~src/modules/companySettings/components/CompanySettingsForm';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import ThreeColsHeader from '~src/shared/headers/threeColsHeader/ThreeColsHeader';
import { fetchAttributesList } from '~src/modules/attributes/actions/attributes.actions';
import ExportAnswersData from '~src/modules/companySettings/components/ExportAnswersData';
import { ApiReduxResp, ThunkAnyAction } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import {
  CompanySettingsAttrsI,
  CompanyResp,
  CompanyRespWithSettingAttributes,
  defaultFeatures,
  Features,
} from '~src/interfaces/company.interface';
import { TimezonesI, ContentLocalesI, SystemLocalesI } from '~src/interfaces/session.types';
import NavArrowLnk from '~src/shared/buttons/NavArrowLnk';
import { updateCompany, updateCompanyAttrs, fetchTimeZones, fetchLocales } from '~src/store/session/actions';
import { routes } from '~src/routingConfig';
import IntegrationsList from '~src/modules/companySettings/importUsers/IntegrationsList';
import CompanyDelete from '~src/modules/companySettings/components/CompanyDelete';
import { PlaceHolder } from '~src/modules/userSettings/userSettings.sc';

import { PageContentContainerExt, MainContainerAnimationExt } from './companySettings.sc';
import {ExportUsersWithOtp} from "~src/modules/companySettings/components/ExportUsersWithOtp";
import {StaticContext} from "react-router";

export type OnSubmitSave = (
  coreValues: { name?: string },
  attrs: CompanySettingsAttrsI,
) => Promise<void | Record<string, string>>;

interface StateProps {
  timezones: TimezonesI;
  contentLocales: ContentLocalesI;
  systemLocales: SystemLocalesI;
  features: Partial<Features>;
  isAuthenticated: boolean;
}

interface DispatchProps {
  fetchTimeZones(): ApiReduxResp<unknown>;
  fetchLocales(): ApiReduxResp<unknown>;
  fetchAttributesList(): ApiReduxResp<AttributeI[]>;
  updateAttributes(vals: CompanySettingsAttrsI): ApiReduxResp<CompanyRespWithSettingAttributes>;
  updateCompany(vals: { name?: string }): ApiReduxResp<CompanyResp>;
}

type Props = StateProps & DispatchProps & WithTranslation & RouteComponentProps<object, StaticContext, { section?: 'import' }>;

class CompanySettings extends React.Component<Props> {
  importSectionRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    if (this.props.isAuthenticated) {
      void this.props.fetchAttributesList();
      void this.props.fetchTimeZones();
      void this.props.fetchLocales();
    }
    if (this.props.location.state) {
      const { section } = this.props.location.state;
      if (section === 'import' && this.importSectionRef.current) {
        this.importSectionRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }
  }

  onSubmitSave: OnSubmitSave = (coreValues, attrs) => {
    const { t } = this.props;

    return Promise.all([this.updateCompany(coreValues), this.updateCompanyAttributes(attrs)])
      .then(() => {
        return;
      })
      .catch((error: AxiosError) => {
        const errors: Record<string, string> = { [FORM_ERROR]: t('x.users.save_error') };
        Object.keys(error.response?.data ?? {}).forEach((key) => {
          errors[key] = error.response?.data[key] ?? '';
        });
        return errors;
      });
  };

  updateCompany(values: { name?: string }): Promise<Record<string, unknown> | ThunkAnyAction<CompanyResp>> {
    return new Promise((resolve) => {
      if (!Object.keys(values).length) {
        resolve({});
        return;
      }
      void this.props.updateCompany(values).then(response => {
        if (response.error) {
          logger.info('Error updating company', {
            error: new Error('error on updating Company'),
            info: {
              componentStack: 'CompanySettings.jsx',
            },
          });
        }
        resolve(response);
      });
    });
  }

  updateCompanyAttributes(
    attributes: CompanySettingsAttrsI,
  ): Promise<Record<string, unknown> | ThunkAnyAction<CompanyRespWithSettingAttributes>> {
    return new Promise((resolve) => {
      if (Object.keys(attributes).length === 0) {
        resolve({});
        return;
      }
      void this.props.updateAttributes(attributes).then((response) => {
        if (response.error) {
          logger.info('Error updating company attributes', {
            error: new Error('error on updating Company'),
            info: {
              componentStack: 'CompanySettings.jsx',
            },
          });
        }
        resolve(response);
      });
    });
  }

  isContentLoading(): boolean {
    return (
      !Object.keys(this.props.timezones).length ||
      !Object.keys(this.props.contentLocales).length ||
      !Object.keys(this.props.systemLocales).length
    );
  }

  onDelCompany = () => {
    this.props.history.push(routes.logout);
  };

  render() {
    const { t } = this.props;
    return (
      <PageContentContainerExt>
        <ThreeColsHeader
          title={{
            key: 'x.company_settings.header.title',
            txt: t('x.company_settings.header.title'),
          }}
          supTitle={{
            key: 'x.company_settings.header.subtitle',
            txt: t('x.company_settings.header.subtitle'),
          }}
          rightColChildren={
            this.props.features.survey ? (
              <Link to={routes.reports.company} style={{ textDecoration: 'none' }}>
                <NavArrowLnk arrOnRight arrowDir="right">
                  <span data-translation="x.company_settings.header.linktitle">
                    {t('x.company_settings.header.linktitle')}
                  </span>
                </NavArrowLnk>
              </Link>
            ) : undefined
          }
        />
        {this.isContentLoading() ? (
          <LoadingSpinner marginTop={60} />
        ) : (
          <MainContainerAnimationExt>
            <CompanySettingsForm
              timezones={this.props.timezones}
              contentLocales={this.props.contentLocales}
              systemLocales={this.props.systemLocales}
              onFrmSubmit={this.onSubmitSave}
            />
          </MainContainerAnimationExt>
        )}
        {this.props.features.survey && <ExportAnswersData />}
        <ExportUsersWithOtp/>
        <div ref={this.importSectionRef}>
          <IntegrationsList />
        </div>
        <CompanyDelete onDelCompany={this.onDelCompany} />
        {this.props.features.survey ? (
          <TextBtnPageFooter
            t={t}
            infoKey="x.company_settings.footer.title"
            btnKey="x.company_settings.footer.button"
            linkUrl={routes.reports.company}
          />
        ) : (
          <PlaceHolder />
        )}
      </PageContentContainerExt>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => {
  const {
    user: { session },
  } = state;
  return {
    timezones: session.timezones,
    contentLocales: session.contentLocales,
    systemLocales: session.systemLocales,
    features: session.company ? session.company.features : defaultFeatures,
    isAuthenticated: Boolean(session.user),
  };
};

const mapDispatchToProps: MapDispatchToPropsFunction<DispatchProps, unknown> = (dispatch): DispatchProps => ({
  fetchTimeZones: () => dispatch(fetchTimeZones()),
  fetchLocales: () => dispatch(fetchLocales()),
  fetchAttributesList: () => dispatch(fetchAttributesList()),
  updateAttributes: (value) => dispatch(updateCompanyAttrs(value)),
  updateCompany: (values) => dispatch(updateCompany(values)),
});

export default _.flow([withTranslation(), connect(mapStateToProps, mapDispatchToProps)])(CompanySettings);
